import { z } from 'zod';

import { AI_EDIT_INSTRUCTION_MAX_LENGTH } from './opportunities.js';
import {
  UPLOAD_PURPOSE_PERSONA_SCRIPT_GUIDELINES,
  UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE,
} from '../constants/upload-policies.js';
import { CreateSetDateWindowSchema } from '../filters/date-window.js';
import { sanitizePostgresText } from '../validation/postgres-text.js';

/** Max script or brief IDs in one AI Edit job (one LLM rewrite per id). */
export const PERSONA_AI_EDIT_IDS_MAX = 20;

// ─── Persona Opportunity Event Payloads ─────────────────────────────
//
// This file defines the *shape of data* carried on Pub/Sub messages for the
// `persona_opportunities` flow. Pair each schema with a name from
// `PersonaOpportunityEvents` in `constants/event-names.ts`.
//
// Who uses this:
//   - services/api        → validates before publish (optional) / types the publish call
//   - opportunities-engine → validates in the handler via `withErrorHandling(schema, …)`
//
// What belongs here vs elsewhere:
//   - HERE: fields on the wire for each event (Pub/Sub message body)
//   - NOT HERE: HTTP Amber base (`idempotencyKey`) → API route schemas
//   - NOT HERE: DB columns → Prisma models
//
// Add one exported Zod schema (and matching `z.infer` type) per event as the
// flow grows.

const positiveIntKnob = z.number().int().positive();

/** Optional tuning overrides — caller must have `PERSONA_OPPORTUNITY_SETS_QUERY_TUNING` at API gate. */
export const PersonaOpportunityGenerateSetAdvancedSchema = z
  .object({
    candidatePoolSize: positiveIntKnob,
    anglesReturned: positiveIntKnob,
    maxAnglesPerFeature: positiveIntKnob,
    minBrandsPerFeature: positiveIntKnob,
    stickDays: positiveIntKnob,
  })
  .strict();
export type PersonaOpportunityGenerateSetAdvanced = z.infer<
  typeof PersonaOpportunityGenerateSetAdvancedSchema
>;

// ── GENERATE_SET_REQUESTED ───────────────────────────────────────────
// Event: PersonaOpportunityEvents.GENERATE_SET_REQUESTED
// When:  user taps Create & Generate (PRD §6).
//
// API validates and publishes this payload. Initial `POST /generate` omits
// `setId` — the worker creates the set row. Retry / regenerate / new_set include
// `setId` so the worker generates into an API-prepared row. `idempotencyKey`
// lives on the envelope.
//
// Keep field shapes aligned with `GeneratePersonaOpportunitySetRequestSchema`
// in services/api/src/schemas/persona-opportunity-sets.schema.ts.
//
// TODO(nexus): requestedBy — Nexus user id for audit / analytics (#AD-2059).
// TODO(nexus): strategic priorities on message vs set row only (#AD-2059).

/** Payload for `PERSONA_OPPORTUNITY.GENERATE_SET_REQUESTED`. */
export const PersonaOpportunityGenerateSetRequestedPayload = z
  .object({
    tenantId: z.string().cuid2(),
    /**
     * Present for retry / overwrite / new_set (API already prepared the row).
     * Absent for first-time `POST /generate` (worker creates the row).
     */
    setId: z.string().min(1).optional(),
    name: z.string().min(1),
    /** Personas to generate for — required ≥1 (PRD §6 Step 2). */
    personaIds: z.array(z.string().min(1)).min(1),
    industry: z.string().min(1),
    industryCompetitors: z.array(z.string().trim().min(1)).min(1),
    /** v1: single ad platform — BQ filters on one @platform until multi-platform SQL lands. */
    adPlatforms: z.array(z.string().trim().min(1)).max(1).optional(),
    advanced: PersonaOpportunityGenerateSetAdvancedSchema.optional(),
  })
  .and(CreateSetDateWindowSchema);

export type PersonaOpportunityGenerateSetRequestedPayload = z.infer<
  typeof PersonaOpportunityGenerateSetRequestedPayload
>;

// ── GENERATE_SCRIPTS_REQUESTED ───────────────────────────────────────
// Event: PersonaOpportunityEvents.GENERATE_SCRIPTS_REQUESTED
// When:  user triggers Generate Scripts (bulk bar) or regenerates one script.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here.
//
// When `scriptId` is set, the worker regenerates that script only (must belong
// to one of `personaOpportunityIds`). Omit `scriptId` to create more scripts
// on the listed opportunities.

export const PERSONA_SCRIPT_GUIDELINES_MAX_CHARS = 10_000;

/** Max plain-text length for imported script bodies (paste, extract, or Amber payload). */
export const PERSONA_SCRIPT_IMPORT_MAX_CHARS = 100_000;

/** Max upload size for imported script files (client check and import/extract multipart). */
export const PERSONA_SCRIPT_IMPORT_MAX_BYTES = 5 * 1024 * 1024;

/** Generate POST 4xx when AD-2147's duration probe rejects a reference video. */
export const REFERENCE_VIDEO_TOO_LONG = 'REFERENCE_VIDEO_TOO_LONG' as const;

export const PersonaOpportunityScriptOutputTypeSchema = z.enum(['script', 'brief', 'both']);
export type PersonaOpportunityScriptOutputType = z.infer<
  typeof PersonaOpportunityScriptOutputTypeSchema
>;

export const PersonaOpportunityScriptsPerAngleSchema = z.number().int().min(1).max(10);

export const PersonaOpportunityVideoReferenceSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('none') }),
  z.object({ kind: z.literal('angle_example') }),
  z.object({ kind: z.literal('upload'), uploadId: z.string().min(1) }),
]);
export type PersonaOpportunityVideoReference = z.infer<
  typeof PersonaOpportunityVideoReferenceSchema
>;

/** Upload purpose string the generate route requires when `videoReference.kind` is `upload`. */
export const PERSONA_SCRIPT_REFERENCE_PURPOSE = UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE;

/** Upload purpose for a guidelines file persisted by extract (`guidelinesUploadId`). */
export const PERSONA_SCRIPT_GUIDELINES_PURPOSE = UPLOAD_PURPOSE_PERSONA_SCRIPT_GUIDELINES;

/** Payload for `PERSONA_OPPORTUNITY.GENERATE_SCRIPTS_REQUESTED`. */
export const PersonaOpportunityGenerateScriptsRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  /** One or more persona opportunity (angle) ids to generate scripts for. */
  personaOpportunityIds: z.array(z.string().min(1)).min(1),
  /**
   * Optional single-script regenerate. When present, the worker asserts the
   * script belongs to one of `personaOpportunityIds` and to `tenantId`.
   */
  scriptId: z.string().min(1).optional(),
  /**
   * Optional guidelines stamped onto newly created scripts in this run.
   * Regenerates keep the existing script row's guidelines unless PATCH'd.
   */
  scriptGuidelines: z.string().trim().min(1).max(PERSONA_SCRIPT_GUIDELINES_MAX_CHARS).optional(),
  /** What the worker should produce. Parsed and forwarded; ignored until AD-2154. */
  outputType: PersonaOpportunityScriptOutputTypeSchema.optional(),
  /** Scripts to create per angle this job when Script is selected. Omit → worker default 5. */
  scriptsPerAngle: PersonaOpportunityScriptsPerAngleSchema.optional(),
  /** Either/or video reference. Parsed and forwarded; ignored until AD-2147. */
  videoReference: PersonaOpportunityVideoReferenceSchema.optional(),
  /** Guidelines file upload id when the modal extracted a PDF/DOCX/TXT. Omit if none. */
  guidelinesUploadId: z.string().min(1).optional(),
});

export type PersonaOpportunityGenerateScriptsRequestedPayload = z.infer<
  typeof PersonaOpportunityGenerateScriptsRequestedPayload
>;

// ── GENERATE_HOOK_VARIATIONS_REQUESTED ───────────────────────────────
// Event: PersonaOpportunityEvents.GENERATE_HOOK_VARIATIONS_REQUESTED
// When:  user clicks + Add 3 hooks on a READY parent persona script.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** LLM must return exactly this many hook variants per suggest job. */
export const HOOK_VARIATIONS_PER_PARENT = 3;

/**
 * Highest `slotIndex` hook-variant allocation may use (occupied indexes,
 * including soft-deleted). Bulk fill-up still uses 5 scripts per angle.
 */
export const MAX_PERSONA_SCRIPT_SLOT_INDEX = 20;

/** Prompt Manager `usedIn` for persona-script hook variations. */
export const PERSONA_SCRIPT_HOOK_VARIATIONS_PROMPT_USED_IN =
  'creative-script-hook-variations-persona';

/** Prompt Manager `usedIn` for import structure mapping (not generate). */
export const PERSONA_SCRIPT_IMPORT_PROMPT_USED_IN = 'creative-script-import-persona-structure';

/** Prompt Manager `usedIn` for persona brief AI Edit (Claude text). */
export const PERSONA_BRIEF_EDIT_PROMPT_USED_IN = 'creative-brief-edit-persona-angles';

/** Prompt Manager `usedIn` for generate-time reference video analysis (Gemini vision). */
export const PERSONA_REFERENCE_VIDEO_ANALYSIS_PROMPT_USED_IN =
  'persona-reference-video-analysis';

/**
 * Default Gemini model for that analysis prompt when none is saved.
 * Use a Vertex-listed economy vision SKU (`gemini-2.5-flash`). `gemini-3.1-flash`
 * is not a publisher model, and `gemini-3.1-flash-lite` is not in this project's catalog.
 */
export const PERSONA_REFERENCE_VIDEO_ANALYSIS_DEFAULT_MODEL = 'gemini-2.5-flash';

/** Payload for `PERSONA_OPPORTUNITY.GENERATE_HOOK_VARIATIONS_REQUESTED`. */
export const PersonaOpportunityGenerateHookVariationsRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  setId: z.string().min(1),
  personaOpportunityId: z.string().min(1),
  parentScriptId: z.string().min(1),
  requestedBy: z.string().min(1),
});

export type PersonaOpportunityGenerateHookVariationsRequestedPayload = z.infer<
  typeof PersonaOpportunityGenerateHookVariationsRequestedPayload
>;

export const PersonaHookVariationSuggestionSchema = z.object({
  strategyLabel: z.string().trim().min(1),
  hook: z.string().trim().min(1),
  scene1Hearing: z.string().trim().min(1),
  scene1Seeing: z.string().trim().min(1).optional(),
});
export type PersonaHookVariationSuggestion = z.infer<typeof PersonaHookVariationSuggestionSchema>;

/** Correlation status result written by the worker; not persisted until confirm. */
export const PersonaHookVariationsStatusResultSchema = z.object({
  parentScriptId: z.string().min(1),
  variants: z.array(PersonaHookVariationSuggestionSchema).length(HOOK_VARIATIONS_PER_PARENT),
});
export type PersonaHookVariationsStatusResult = z.infer<
  typeof PersonaHookVariationsStatusResultSchema
>;

const HookVariationKeptIndexSchema = z.number().int().min(0).max(HOOK_VARIATIONS_PER_PARENT - 1);

/** HTTP confirm body — `keptIndexes` are unique variant positions 0..2. */
export const PersonaOpportunityConfirmHookVariationsBody = z.object({
  tenantId: z.string().cuid2(),
  idempotencyKey: z.string().min(1),
  correlationId: z.string().min(1),
  parentScriptId: z.string().min(1),
  keptIndexes: z
    .array(HookVariationKeptIndexSchema)
    .max(HOOK_VARIATIONS_PER_PARENT)
    .refine((indexes) => new Set(indexes).size === indexes.length, {
      message: 'keptIndexes must be unique',
    }),
});
export type PersonaOpportunityConfirmHookVariationsBody = z.infer<
  typeof PersonaOpportunityConfirmHookVariationsBody
>;

// ── AI_EDIT_SCRIPTS_REQUESTED ────────────────────────────────────────
// Event: PersonaOpportunityEvents.AI_EDIT_SCRIPTS_REQUESTED
// When:  user submits an AI Edit instruction on one or more persona scripts.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** Optional per-cell target — when set, `scriptIds` must contain exactly one id.
 *
 * Mixed-version deploys: older opportunities-engine Zod schemas strip unknown
 * `cell`, then run a whole-script AI edit. Deploy opportunities-engine (this
 * payload + handler) before or together with API/FE that publish `cell`. Do not
 * roll out the API `cell` path alone.
 */
export const PersonaScriptAiEditCellSchema = z.object({
  sceneNumber: z.number().int().nonnegative(),
  field: z.enum(['seeing', 'hearing', 'product', 'props']),
});
export type PersonaScriptAiEditCell = z.infer<typeof PersonaScriptAiEditCellSchema>;

/** Payload for `PERSONA_OPPORTUNITY.AI_EDIT_SCRIPTS_REQUESTED`. */
export const PersonaOpportunityAiEditScriptsRequestedPayload = z
  .object({
    tenantId: z.string().cuid2(),
    requestedBy: z.string().min(1),
    setId: z.string().min(1),
    scriptIds: z.array(z.string().min(1)).min(1).max(PERSONA_AI_EDIT_IDS_MAX),
    instruction: z.string().min(1).max(AI_EDIT_INSTRUCTION_MAX_LENGTH),
    cell: PersonaScriptAiEditCellSchema.optional(),
  })
  .refine((data) => data.cell === undefined || data.scriptIds.length === 1, {
    message: 'cell requires exactly one scriptId',
    path: ['scriptIds'],
  });
export type PersonaOpportunityAiEditScriptsRequestedPayload = z.infer<
  typeof PersonaOpportunityAiEditScriptsRequestedPayload
>;

// ── AI_EDIT_BRIEFS_REQUESTED ─────────────────────────────────────────
// Event: PersonaOpportunityEvents.AI_EDIT_BRIEFS_REQUESTED
// When:  user submits an AI Edit instruction on one or more persona briefs.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** Optional per-cell target — never `videoName` / title.
 *
 * Mixed-version deploys: older opportunities-engine Zod schemas strip unknown
 * `cell`, then run a whole-brief AI edit. Deploy opportunities-engine (this
 * payload + handler) before or together with API/FE that publish `cell`. Do not
 * roll out the API `cell` path alone.
 */
export const PersonaBriefAiEditCellSchema = z.discriminatedUnion('target', [
  z.object({
    target: z.literal('hook'),
    hookIndex: z.number().int().min(1).max(4),
    field: z.enum(['direction', 'line']),
  }),
  z.object({
    target: z.literal('concept'),
    field: z.enum(['direction', 'line']),
  }),
  z.object({
    target: z.literal('cta'),
    field: z.enum(['direction', 'line']),
  }),
  z.object({ target: z.literal('notes') }),
  z.object({ target: z.literal('preferences') }),
]);
export type PersonaBriefAiEditCell = z.infer<typeof PersonaBriefAiEditCellSchema>;

/** Payload for `PERSONA_OPPORTUNITY.AI_EDIT_BRIEFS_REQUESTED`. */
export const PersonaOpportunityAiEditBriefsRequestedPayload = z
  .object({
    tenantId: z.string().cuid2(),
    requestedBy: z.string().min(1),
    setId: z.string().min(1),
    briefIds: z.array(z.string().min(1)).min(1).max(PERSONA_AI_EDIT_IDS_MAX),
    instruction: z.string().min(1).max(AI_EDIT_INSTRUCTION_MAX_LENGTH),
    cell: PersonaBriefAiEditCellSchema.optional(),
  })
  .refine((data) => data.cell === undefined || data.briefIds.length === 1, {
    message: 'cell requires exactly one briefId',
    path: ['briefIds'],
  });
export type PersonaOpportunityAiEditBriefsRequestedPayload = z.infer<
  typeof PersonaOpportunityAiEditBriefsRequestedPayload
>;

// ── STORYBOARD_GENERATION_REQUESTED ──────────────────────────────────
// Event: PersonaOpportunityEvents.STORYBOARD_GENERATION_REQUESTED
// When:  user clicks Generate storyboard on a READY persona script.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Same domain topic as generate-scripts.

/** Payload for `PERSONA_OPPORTUNITY.STORYBOARD_GENERATION_REQUESTED`. */
export const PersonaOpportunityStoryboardGenerationRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  setId: z.string().min(1),
  scriptId: z.string().min(1),
  /** Sketch sheet or photoreal animatic — same variants as legacy opportunity scripts. */
  variant: z.enum(['sketch', 'panels']).default('sketch'),
});
export type PersonaOpportunityStoryboardGenerationRequestedPayload = z.infer<
  typeof PersonaOpportunityStoryboardGenerationRequestedPayload
>;

// ── SUGGEST_SCRIPTS_REQUESTED ────────────────────────────────────────
// Event: PersonaOpportunityEvents.SUGGEST_SCRIPTS_REQUESTED
// When:  user submits Add your script on the persona Scripts tab.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Worker finds-or-creates the set's
// synthetic custom angle, then generates N scripts onto it.

export const PERSONA_SUGGEST_SCRIPT_DESCRIPTION_MAX_CHARS = 10_000;
export const PERSONA_SUGGEST_SCRIPT_VARIATIONS_MAX = 5;
export const PERSONA_SUGGEST_SCRIPT_REFERENCE_URLS_MAX = 3;

/** Payload for `PERSONA_OPPORTUNITY.SUGGEST_SCRIPTS_REQUESTED`. */
export const PersonaOpportunitySuggestScriptsRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  requestedBy: z.string().min(1),
  setId: z.string().min(1),
  description: z.string().trim().min(1).max(PERSONA_SUGGEST_SCRIPT_DESCRIPTION_MAX_CHARS),
  variations: z.number().int().min(1).max(PERSONA_SUGGEST_SCRIPT_VARIATIONS_MAX),
  referenceUrls: z.array(z.string().url()).max(PERSONA_SUGGEST_SCRIPT_REFERENCE_URLS_MAX).default([]),
});
export type PersonaOpportunitySuggestScriptsRequestedPayload = z.infer<
  typeof PersonaOpportunitySuggestScriptsRequestedPayload
>;

// ── IMPORT_SCRIPT_FROM_TEXT_REQUESTED ────────────────────────────────
// Event: PersonaOpportunityEvents.IMPORT_SCRIPT_FROM_TEXT_REQUESTED
// When:  user confirms Import on the persona Scripts tab.
//
// API validates and publishes this payload. `idempotencyKey` / `correlationId`
// live on the Pub/Sub envelope, not here. Worker maps structure via LLM, then
// persists one script whose Full Script is `text`.

/** Payload for `PERSONA_OPPORTUNITY.IMPORT_SCRIPT_FROM_TEXT_REQUESTED`. */
export const PersonaOpportunityImportScriptFromTextRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  requestedBy: z.string().min(1),
  setId: z.string().min(1),
  text: z
    .string()
    .transform(sanitizePostgresText)
    .pipe(z.string().trim().min(1).max(PERSONA_SCRIPT_IMPORT_MAX_CHARS)),
});
export type PersonaOpportunityImportScriptFromTextRequestedPayload = z.infer<
  typeof PersonaOpportunityImportScriptFromTextRequestedPayload
>;

// ── RESOLVE_REFERENCE_LINKS_REQUESTED ────────────────────────────────
// Event: PersonaOpportunityEvents.RESOLVE_REFERENCE_LINKS_REQUESTED
// When:  Suggest Script modal pre-fetches pasted URLs before generation.

/** Payload for `PERSONA_OPPORTUNITY.RESOLVE_REFERENCE_LINKS_REQUESTED`. */
export const PersonaOpportunityResolveReferenceLinksRequestedPayload = z.object({
  tenantId: z.string().cuid2(),
  requestedBy: z.string().min(1),
  setId: z.string().min(1),
  referenceUrls: z.array(z.string().url()).max(PERSONA_SUGGEST_SCRIPT_REFERENCE_URLS_MAX).default([]),
});
export type PersonaOpportunityResolveReferenceLinksRequestedPayload = z.infer<
  typeof PersonaOpportunityResolveReferenceLinksRequestedPayload
>;
